import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight, AlertTriangle, Users } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { CTAButton } from "./CTAButton";
import { CYCLES } from "../data/cycles";

export function MobilityCycleSelector() {
  const { t } = useTranslation();
  const [active, setActive] = useState(CYCLES[0].id);
  const cycle = CYCLES.find((c) => c.id === active);
  const route = t(`data.cycles.${cycle.id}.route`, { returnObjects: true });
  const planName = t(`data.plans.${cycle.planId}.name`);

  return (
    <section className="py-24 md:py-32 border-t border-white/5" data-testid="mobility-cycles">
      <div className="container-x">
        <Reveal>
          <Overline>{t("cycles.overline")}</Overline>
          <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-3xl">{t("cycles.title")}</h2>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 flex flex-col gap-3">
            {CYCLES.map((c) => (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                data-testid={`cycle-tab-${c.id}`}
                className={`text-left p-5 rounded-xl border transition-all ${
                  active === c.id ? "bg-navy-700 border-signal/40 shadow-[0_0_28px_rgba(0,85,255,0.15)]" : "bg-white/[0.02] border-white/10 hover:border-white/20"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-gold">{c.id}</span>
                  {active === c.id && <span className="w-2 h-2 rounded-full bg-signal" />}
                </div>
                <p className="mt-1.5 font-display font-semibold text-white">{t(`data.cycles.${c.id}.label`)}</p>
              </button>
            ))}
          </div>

          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={cycle.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
                className="surface p-8 h-full"
                data-testid={`cycle-panel-${cycle.id}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  {route.map((leg, i) => (
                    <React.Fragment key={i}>
                      <span className="px-3 py-1.5 rounded-md bg-navy-600 border border-white/10 text-sm">{leg}</span>
                      {i < route.length - 1 && <ArrowRight size={14} className="text-signal/60 rtl:rotate-180" />}
                    </React.Fragment>
                  ))}
                </div>

                <div className="mt-8 grid sm:grid-cols-2 gap-6">
                  <Detail icon={Users} label={t("cycles.whoServes")} value={t(`data.cycles.${cycle.id}.serves`)} />
                  <Detail icon={AlertTriangle} label={t("cycles.mainRisks")} value={t(`data.cycles.${cycle.id}.risks`)} accent="gold" />
                </div>

                <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-6 border-t border-white/10">
                  <div>
                    <p className="overline text-white/40">{t("common.recommendedPlan")}</p>
                    <p className="mt-1 font-display font-semibold text-lg text-gold">{planName}</p>
                  </div>
                  <CTAButton to={`/quote?cycle=${cycle.id}&plan=${cycle.planId}`} variant="primary" testId={`cycle-cta-${cycle.id}`}>
                    {t("common.buildPlan")} <ArrowRight size={16} className="rtl:rotate-180" />
                  </CTAButton>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

function Detail({ icon: Icon, label, value, accent = "signal" }) {
  return (
    <div className="flex gap-3">
      <div className={`shrink-0 w-9 h-9 rounded-lg grid place-items-center ${accent === "gold" ? "bg-gold/10 text-gold" : "bg-signal/10 text-signal"}`}>
        <Icon size={16} />
      </div>
      <div>
        <p className="overline text-white/40">{label}</p>
        <p className="mt-1.5 text-sm text-white/70 leading-relaxed">{value}</p>
      </div>
    </div>
  );
}

export default MobilityCycleSelector;
